import { apiService } from './api';

export interface AttendanceRecord {
  id: number;
  employeeId: number;
  employeeName: string;
  /** DateOnly — "YYYY-MM-DD". */
  workDate: string;
  clockInAt: string;
  clockOutAt?: string | null;
  /** TimeSpan — "HH:mm:ss"; null while the record is still open. */
  hoursWorked?: string | null;
  notes?: string;
}

export interface AttendanceFilter {
  pageNumber?: number;
  pageSize?: number;
  employeeId?: number;
  /** "YYYY-MM-DD" — the API compares against the work date, not the clock-in instant. */
  fromDate?: string;
  toDate?: string;
}

export interface ClockInDTO {
  employeeId: number;
  notes?: string;
}

export interface ClockOutDTO {
  notes?: string;
}

/**
 * Clocking someone other than yourself needs AttendanceRecords.ManageOthers on the
 * server side; the API answers 403 when the caller only holds ClockInOut.
 */
const attendanceService = {
  getAll: async (filter: AttendanceFilter = {}) => {
    const response = await apiService.get<AttendanceRecord[]>('/hr/attendance', {
      pageNumber: filter.pageNumber ?? 1,
      pageSize: filter.pageSize ?? 100,
      employeeId: filter.employeeId,
      fromDate: filter.fromDate,
      toDate: filter.toDate,
    });
    return {
      items: response.data.data ?? [],
      pagination: response.data.pagination,
    };
  },

  /** Returns null when the employee is not currently clocked in. */
  getOpenRecord: async (employeeId: number) => {
    const response = await apiService.get<AttendanceRecord | null>(`/hr/attendance/open/${employeeId}`);
    return response.data.data ?? null;
  },

  clockIn: async (dto: ClockInDTO) => {
    const response = await apiService.post<AttendanceRecord>('/hr/attendance/clock-in', dto);
    return response.data.data;
  },

  clockOut: async (id: number, dto: ClockOutDTO) => {
    const response = await apiService.post<AttendanceRecord>(`/hr/attendance/${id}/clock-out`, dto);
    return response.data.data;
  },
};

export default attendanceService;
